import React, { useState } from 'react';
import { Music, BookOpen, Coffee, MapPin, Clock, Calendar, Send, Check, Users, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DBUPage = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', guest_count: 1,
    year_of_study: '', how_heard: '', message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const resp = await fetch(`${BACKEND_URL}/api/rsvps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          guest_count: parseInt(formData.guest_count) || 1,
          source: 'dbu-campus',
          campus: 'DBU',
        }),
      });
      if (!resp.ok) throw new Error('Failed');
      setIsSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or email contact@elbethel.church');
    } finally {
      setIsSubmitting(false);
    }
  };

  const highlights = [
    { icon: Music, title: 'Live Worship', desc: 'Acoustic worship to reset your week' },
    { icon: BookOpen, title: 'Bible Study', desc: 'Relevant, expository teaching' },
    { icon: Sparkles, title: 'Activities', desc: 'Interactive and reflective moments' },
    { icon: Coffee, title: 'Free Food', desc: 'Dinner provided every session' },
  ];

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0 min-h-screen bg-[#fafaf8]">
      {/* Hero */}
      <section className="relative py-10 sm:py-14 lg:py-20 overflow-hidden" style={{background: 'linear-gradient(135deg, #003D79 0%, #00264d 60%, #001a33 100%)'}}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border border-white/20 rounded-full" />
          <div className="absolute bottom-10 right-10 w-60 h-60 border border-white/10 rounded-full" />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-5">
            <MapPin className="w-3.5 h-3.5 text-amber-300" />
            <span className="text-xs text-white/90 font-medium tracking-wide">Dallas Baptist University</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 font-display leading-tight" data-testid="dbu-title">
            Worship & Bible Study
          </h1>
          <p className="text-sm sm:text-base text-white/80 mb-6 max-w-xl mx-auto leading-relaxed">
            A midweek gathering for students to worship, study God&apos;s Word, connect,
            and recharge. Music, meditation, activities, and dinner — all in one hour.
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-xs sm:text-sm">
            <span className="bg-white/15 backdrop-blur-sm text-white rounded-full px-4 py-2 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-amber-300" /> Tuesdays or Thursdays (TBD)
            </span>
            <span className="bg-white/15 backdrop-blur-sm text-white rounded-full px-4 py-2 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-amber-300" /> 6:00 – 7:00 PM
            </span>
            <span className="bg-white/15 backdrop-blur-sm text-white rounded-full px-4 py-2 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-amber-300" /> Rogers Theater, Pilgrim Chapel
            </span>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-8 sm:py-10">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {highlights.map((h, i) => (
              <div key={i} className="bg-white rounded-xl border border-stone-200 p-4 text-center hover:shadow-md transition-shadow">
                <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-2">
                  <h.icon className="w-5 h-5 text-[#003D79]" />
                </div>
                <h3 className="font-bold text-stone-800 text-xs sm:text-sm mb-0.5">{h.title}</h3>
                <p className="text-[10px] sm:text-xs text-stone-500">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RSVP + Info */}
      <section className="py-6 sm:py-10">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

            {/* RSVP Form */}
            <div className="lg:col-span-3 order-1">
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5 sm:p-7">
                {!isSubmitted ? (
                  <>
                    <div className="flex items-center gap-2.5 mb-1">
                      <Send className="w-5 h-5 text-[#003D79]" />
                      <h2 className="text-lg sm:text-xl font-bold text-stone-800 font-display">Reserve Your Spot</h2>
                    </div>
                    <p className="text-xs text-stone-500 mb-5">
                      Quick RSVP — takes 30 seconds. We&apos;ll send you a reminder before each session.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="dbu-rsvp-form">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-stone-600 mb-1">Name *</label>
                          <input type="text" name="name" required value={formData.name} onChange={handleChange}
                            placeholder="First & Last" data-testid="dbu-name"
                            className="w-full p-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#003D79] focus:border-transparent text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-stone-600 mb-1">Email *</label>
                          <input type="email" name="email" required value={formData.email} onChange={handleChange}
                            placeholder="your@dbu.edu" data-testid="dbu-email"
                            className="w-full p-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#003D79] focus:border-transparent text-sm" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-stone-600 mb-1">Phone <span className="text-stone-400 font-normal">(optional)</span></label>
                          <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                            placeholder="(555) 123-4567" data-testid="dbu-phone"
                            className="w-full p-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#003D79] focus:border-transparent text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-stone-600 mb-1">Year</label>
                          <select name="year_of_study" value={formData.year_of_study} onChange={handleChange} data-testid="dbu-year"
                            className="w-full p-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#003D79] focus:border-transparent text-sm bg-white">
                            <option value="">Select...</option>
                            <option value="Freshman">Freshman</option>
                            <option value="Sophomore">Sophomore</option>
                            <option value="Junior">Junior</option>
                            <option value="Senior">Senior</option>
                            <option value="Graduate">Graduate</option>
                            <option value="Faculty/Staff">Faculty / Staff</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-stone-600 mb-1">Bringing friends?</label>
                          <select name="guest_count" value={formData.guest_count} onChange={handleChange} data-testid="dbu-guests"
                            className="w-full p-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#003D79] focus:border-transparent text-sm bg-white">
                            <option value={1}>Just me</option>
                            {[2,3,4,5,6,7,8].map(n => <option key={n} value={n}>Me + {n-1}</option>)}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-stone-600 mb-1">How did you hear about this?</label>
                        <select name="how_heard" value={formData.how_heard} onChange={handleChange} data-testid="dbu-how-heard"
                          className="w-full p-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#003D79] focus:border-transparent text-sm bg-white">
                          <option value="">Select...</option>
                          <option value="Friend">A friend told me</option>
                          <option value="Social Media">Social media</option>
                          <option value="Flyer/Poster">Flyer or poster on campus</option>
                          <option value="Chapel Announcement">Chapel announcement</option>
                          <option value="Professor/Staff">Professor or staff</option>
                          <option value="QR Code">Scanned a QR code</option>
                          <option value="Website">Found online</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-stone-600 mb-1">
                          Anything else? <span className="text-stone-400 font-normal">(optional)</span>
                        </label>
                        <textarea name="message" rows="2" value={formData.message} onChange={handleChange}
                          placeholder="Prayer requests, dietary needs, questions..." data-testid="dbu-message"
                          className="w-full p-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#003D79] focus:border-transparent text-sm resize-none" />
                      </div>

                      {error && <p className="text-red-600 text-xs" data-testid="dbu-error">{error}</p>}

                      <button type="submit" disabled={isSubmitting} data-testid="dbu-submit"
                        className="w-full text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
                        style={{background: '#003D79'}}>
                        {isSubmitting ? 'Sending...' : <><Send className="w-4 h-4" /> Count Me In</>}
                      </button>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-8" data-testid="dbu-success">
                    <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Check className="w-7 h-7 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-bold text-stone-800 mb-2 font-display">You&apos;re In!</h3>
                    <p className="text-sm text-stone-600 mb-2 max-w-sm mx-auto">
                      We&apos;ll send you a reminder with the final day, time, and location details before the first session.
                    </p>
                    <p className="text-xs text-stone-500 italic">See you on campus!</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-5 order-2">
              <div className="bg-white rounded-2xl border border-stone-200 p-5">
                <h3 className="font-bold text-stone-800 text-sm mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#003D79]" /> Location
                </h3>
                <p className="text-xs text-stone-700 font-medium">Rogers Theater</p>
                <p className="text-xs text-stone-500 mb-2">Below Patty &amp; Bo Pilgrim Chapel, DBU Campus</p>
                <div className="bg-blue-50 rounded-lg p-3 text-xs text-[#003D79]">
                  <p className="font-semibold mb-0.5">Day &amp; time finalizing soon!</p>
                  <p className="text-[10px] opacity-80">We&apos;re confirming Tuesdays or Thursdays, 6–7 PM. RSVP and we&apos;ll notify you the moment it&apos;s set.</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-5">
                <h3 className="font-bold text-stone-800 text-sm mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#003D79]" /> Who is this for?
                </h3>
                <ul className="text-xs text-stone-600 space-y-1.5">
                  <li>&#10003; All DBU students &amp; faculty welcome</li>
                  <li>&#10003; Any denomination or background</li>
                  <li>&#10003; Bring friends from any campus</li>
                  <li>&#10003; No commitment — come when you can</li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-5 text-center">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Invite a friend</p>
                <img src="/dbu-qr.png" alt="Scan to RSVP for DBU worship" className="w-28 h-28 mx-auto mb-1" data-testid="dbu-qr-code" />
                <p className="text-[10px] text-stone-400">elbethel.church/dbu</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hosted by */}
      <section className="py-6 sm:py-8">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <p className="text-xs text-stone-400 uppercase tracking-wider mb-2">Hosted by</p>
          <p className="text-sm font-semibold text-stone-700">El Bethel Church</p>
          <p className="text-xs text-stone-500 mt-1">
            A community rooted in God&apos;s Word, meeting weekly in Grand Prairie, TX.
          </p>
          <a href="/" className="text-xs text-[#003D79] hover:underline mt-1 inline-block">Learn more about El Bethel →</a>
        </div>
      </section>
    </div>
  );
};

export default DBUPage;
